import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCategoryFilter } from '../context/CategoryFilterContext';
import styled from 'styled-components';
import ProductCard from '../components/product/ProductCard';
import { Product, Category, Family, Subfamily } from '../types';
import { productService } from '../services/productService';
import { categoryService } from '../services/categoryService';
import { familyService } from '../services/familyService';
import { subfamilyService } from '../services/subfamilyService';
import { toast } from 'react-toastify';
import { categoryHistory } from '../utils/categoryHistory';
import { FaHistory, FaFilter, FaChevronDown } from 'react-icons/fa';
import { getImageUrl } from '../config';
import { useSettings } from '../context/SettingsContext';

// Función para obtener colores dinámicos
const getColors = (settings: any) => ({
  primary: settings?.header_primary_color || "#FDBF37",
  primaryDark: settings?.header_secondary_color || "#242A5B",
  primaryDarker: "#D45212",
  white: "#ffffff",
  black: "#333333",
  gray: "#666666",
  lightGray: "#f5f5f5",
  border: "#e9e9e9",
  success: "#4CAF50",
  danger: "#FF0000",
});

// Modificar estos componentes estilizados
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

// Nuevo componente para la estructura de dos columnas
const ProductsLayout = styled.div`
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-top: 1rem;
  }
`;

// Modificar el contenedor de filtros para que sea una columna vertical
const FiltersContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOpen'].includes(prop)
})<{ isOpen?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  height: fit-content;
  border: 1px solid #e9ecef;
  
  @media (max-width: 768px) {
    padding: 1rem;
    gap: 1rem;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    margin-bottom: 0;
    border-radius: 0;
    height: 100vh;
    overflow-y: auto;
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform 0.3s ease;
    background: white;
  }
`;

// Botón para mostrar filtros en móvil
const MobileFilterButton = styled.button`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: linear-gradient(135deg, #242A5B 0%, #242A5B 100%);
    color: white;
    border: none;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    margin-bottom: 1rem;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
`;

// Título de sección de filtros
const FilterSectionTitle = styled.h3`
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:first-child {
    margin-top: 0;
  }
`;

// Contenedor para cada grupo de filtros
const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #667eea;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
  }
`;

const FilterLabel = styled.label`
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &:hover {
    border-color: #667eea;
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 0.8rem;
    font-size: 0.9rem;
    padding-right: 2rem;
  }
`;

const SearchInput = styled.input`
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  font-size: 1rem;
  flex: 1;
  // min-width: 250px;
  transition: all 0.3s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &:hover {
    border-color: #667eea;
  }
`;

const DynamicSearchInput = styled.input.withConfig({
  shouldForwardProp: (prop) => !['colors'].includes(prop)
})<{ colors: any }>`
  padding: 0.75rem 1rem;
  border: 2px solid ${(props) => props.colors.border};
  border-radius: 10px;
  font-size: 1rem;
  flex: 1;
  transition: all 0.3s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: ${(props) => props.colors.primary};
    box-shadow: 0 0 0 3px ${(props) => props.colors.primary}20;
  }
  
  &:hover {
    border-color: ${(props) => props.colors.primary};
  }
  
  &::placeholder {
    color: #9ca3af;
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 0.8rem;
    font-size: 0.9rem;
    min-width: auto;
    width: 100%;
  }
`;

// Contador de productos filtrados
const FilterResultsCount = styled.div`
  background: linear-gradient(135deg, #242A5B 0%, #242A5B 100%);
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  text-align: center;
  font-weight: 600;
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

// Botón para limpiar filtros
const ClearFiltersButton = styled.button`
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  color: #6c757d;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e9ecef;
    color: #495057;
    border-color: #adb5bd;
  }
  
  &:active {
    transform: translateY(1px);
  }
 `;

// Botón para aplicar filtros en móvil
const ApplyFiltersButton = styled.button`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 1rem;
    background: linear-gradient(135deg, #242A5B 0%, #242A5B 100%);
    color: white;
    border: none;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    margin-top: 1.5rem;
    width: 100%;
    font-size: 1rem;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
`;

// Header para filtros en móvil
const MobileFilterHeader = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 1rem;
    border-bottom: 2px solid #e9ecef;
    margin-bottom: 1rem;
  }
`;

const MobileFilterTitle = styled.h3`
  margin: 0;
  color: #2c3e50;
  font-size: 1.2rem;
  font-weight: 700;
`;

const MobileFilterClose = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #7f8c8d;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    color: #2c3e50;
    background: #f8f9fa;
  }
`;

// Overlay para filtros móvil
const MobileFilterOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOpen'].includes(prop)
})<{ isOpen?: boolean }>`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

// Contenedor para la sección de productos
const ProductsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const Title = styled.h1`
  color:rgb(80, 44, 44);
  font-size: 2.5rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg,#FDBF37 0%, #FDBF37 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 0.5rem;
  }
`;

const Subtitle = styled.p`
  color: #7f8c8d;
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 1rem;
  }
`;
 const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 1rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.8rem;
  }
`;

const NoProducts = styled.div`
  text-align: center;
  padding: 3rem;
  color: #7f8c8d;
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
    font-size: 1rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: #7f8c8d;
  
  @media (max-width: 768px) {
    padding: 2rem;
    font-size: 1rem;
  }
`;
// Agregar estos styled components después de los existentes
const VisitedCategoriesSection = styled.div`
  margin-bottom: 3rem;
  padding: 2rem;
  background: var(--gray-light);
  border-radius: 12px;
`;

const VisitedCategoriesTitle = styled.h2`
  font-size: 1.8rem;
  color: var(--primary-color);
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const CategoryCard = styled.div`
  cursor: pointer;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const CategoryImage = styled.div<{ imageUrl: string }>`
  width: 100%;
  height: 200px;
  background-image: url(\${props => props.imageUrl});
  background-size: cover;
  background-position: center;
  border-radius: 12px;
  margin-bottom: 1rem;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
  }
`;

const CategoryName = styled.h3`
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  right: 1rem;
  color: white;
  font-size: 1.5rem;
  z-index: 1;
  margin: 0;
`;

// Agregar nuevos styled components para paginación
const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: 3rem 0;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
    margin: 2rem 0;
  }
`;

const PaginationButton = styled.button<{ active?: boolean; disabled?: boolean }>`
  padding: 0.75rem 1rem;
  border: 2px solid ${props => props.active ? 'var(--primary-blue)' : '#ecf0f1'};
  background: ${props => props.active ? 'var(--primary-blue)' : 'white'};
  color: ${props => props.active ? 'white' : '#2c3e50'};
  border-radius: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-weight: 600;
  transition: all 0.3s ease;
  opacity: ${props => props.disabled ? 0.5 : 1};
  min-width: 45px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${props => props.active ? '#081050' : 'var(--primary-blue)'};
    color: white;
    border-color: var(--primary-blue);
  }

  &:disabled {
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    min-width: 40px;
    font-size: 0.9rem;
  }
`;

const PaginationInfo = styled.div`
  color: #7f8c8d;
  font-size: 0.9rem;
  text-align: center;
  margin: 1rem 0;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin: 0.5rem 0;
  }
`;

const ProductsPerPageSelect = styled.select`
  padding: 0.5rem;
  border: 2px solid #ecf0f1;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem;
    font-size: 0.8rem;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const Products: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedCategoryFilter, clearCategoryFilter } = useCategoryFilter();
  const { settings } = useSettings();
  const colors = getColors(settings);
  const [products, setProducts] = useState<Product[]>([]);
  const [categoryList, setCategoryList] = useState<Category[]>([]);
  const [familyList, setFamilyList] = useState<Family[]>([]);
  const [subfamilyList, setSubfamilyList] = useState<Subfamily[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [familyFilter, setFamilyFilter] = useState('all');
  const [subfamilyFilter, setSubfamilyFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('name');
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [allCategories, setAllCategories] = useState<string[]>(['all']);
  const [availableFamilies, setAvailableFamilies] = useState<Family[]>([]);
  const [availableSubfamilies, setAvailableSubfamilies] = useState<Subfamily[]>([]);
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(12);
  const [totalProducts, setTotalProducts] = useState(0);
  
  // Estado para filtros móviles
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  // Calcular productos para la página actual
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  // Funciones para filtros móviles
  const toggleMobileFilters = () => {
    setIsMobileFiltersOpen(!isMobileFiltersOpen);
  };

  const closeMobileFilters = () => {
    setIsMobileFiltersOpen(false);
  };

  // Función para aplicar filtros y cerrar el panel móvil
  const applyFiltersAndClose = () => {
    // Los filtros ya se aplican automáticamente por los useEffect
    // Solo necesitamos cerrar el panel móvil
    setIsMobileFiltersOpen(false);
  };

  // Función para limpiar todos los filtros
  const clearAllFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setFamilyFilter('all');
    setSubfamilyFilter('all');
    setSortOrder('name');
    setCurrentPage(1);
    
    // Limpiar parámetros de URL (excepto búsqueda para evitar recargas)
    updateURLParams({
      categoria: null,
      familia: null,
      subfamilia: null
    });
  };

  // Función para actualizar parámetros de URL
  const updateURLParams = (newParams: { [key: string]: string | null }) => {
    const currentParams = new URLSearchParams(searchParams);
    
    Object.entries(newParams).forEach(([key, value]) => {
      if (value && value !== 'all') {
        currentParams.set(key, value);
      } else {
        currentParams.delete(key);
      }
    });
    
    setSearchParams(currentParams);
  };



  // Función para manejar cambio de categoría
  const handleCategoryChange = (categoryValue: string) => {
    setCategoryFilter(categoryValue);
    setFamilyFilter('all');
    setSubfamilyFilter('all');
    setCurrentPage(1);
    
    // Actualizar URL
    updateURLParams({
      categoria: categoryValue,
      familia: null,
      subfamilia: null
    });
  };

  // Función para manejar cambio de familia
  const handleFamilyChange = (familyValue: string) => {
    setFamilyFilter(familyValue);
    setSubfamilyFilter('all');
    setCurrentPage(1);
    
    // Actualizar URL
    updateURLParams({
      familia: familyValue,
      subfamilia: null
    });
  };

  // Función para manejar cambio de subfamilia
  const handleSubfamilyChange = (subfamilyValue: string) => {
    setSubfamilyFilter(subfamilyValue);
    setCurrentPage(1);
    
    // Actualizar URL
    updateURLParams({
      subfamilia: subfamilyValue
    });
  };

  // Efecto para cerrar filtros móviles al cambiar el tamaño de pantalla
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileFiltersOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Efecto para prevenir scroll del body cuando los filtros móviles están abiertos
  useEffect(() => {
    if (isMobileFiltersOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup al desmontar el componente
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileFiltersOpen]);

  // Efecto para cargar categorías
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const fetchedCategories = await categoryService.getCategories();
        setCategoryList(fetchedCategories);
        const categoryNames = ['all', ...fetchedCategories.map(cat => cat.name)];
        setAllCategories(categoryNames);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Efecto para cargar familias cuando cambia la categoría
  useEffect(() => {
    const fetchFamilies = async () => {
      try {
        if (categoryFilter === 'all') {
          const allFamilies = await familyService.getFamilies();
          setFamilyList(allFamilies);
          setAvailableFamilies(allFamilies);
        } else {
          const selectedCategory = categoryList.find(cat => cat.name === categoryFilter);
          if (selectedCategory) {
            const familiesForCategory = await familyService.getFamilies(selectedCategory.id);
            setFamilyList(familiesForCategory);
            setAvailableFamilies(familiesForCategory);
          }
        }
      } catch (error) {
        console.error('Error loading families:', error);
        setFamilyList([]);
        setAvailableFamilies([]);
      }
    };

    if (categoryList.length > 0) {
      fetchFamilies();
    }
  }, [categoryFilter, categoryList]);

  // Efecto para cargar subfamilias cuando cambia la familia
  useEffect(() => {
    const fetchSubfamilies = async () => {
      try {
        if (familyFilter === 'all') {
          if (categoryFilter === 'all') {
            const allSubfamilies = await subfamilyService.getSubfamilies();
            setSubfamilyList(allSubfamilies);
            setAvailableSubfamilies(allSubfamilies);
          } else {
            // Obtener subfamilias de todas las familias de la categoría seleccionada
            const subfamiliesPromises = availableFamilies.map(family => 
              subfamilyService.getSubfamilies(family.id)
            );
            const subfamiliesArrays = await Promise.all(subfamiliesPromises);
            const allSubfamilies = subfamiliesArrays.flat();
            setSubfamilyList(allSubfamilies);
            setAvailableSubfamilies(allSubfamilies);
          }
        } else {
          const selectedFamily = availableFamilies.find(fam => fam.name === familyFilter);
          if (selectedFamily) {
            const subfamiliesForFamily = await subfamilyService.getSubfamilies(selectedFamily.id);
            setSubfamilyList(subfamiliesForFamily);
            setAvailableSubfamilies(subfamiliesForFamily);
          }
        }
      } catch (error) {
        console.error('Error loading subfamilies:', error);
        setSubfamilyList([]);
        setAvailableSubfamilies([]);
      }
    };

    if (availableFamilies.length > 0 || familyFilter === 'all') {
      fetchSubfamilies();
    }
  }, [familyFilter, availableFamilies, categoryFilter]);

  // Efecto para sincronizar selectedCategoryFilter del contexto con el estado local
  useEffect(() => {
    if (selectedCategoryFilter && selectedCategoryFilter !== categoryFilter) {
      console.log('🔄 Syncing context category filter:', selectedCategoryFilter);
      setCategoryFilter(selectedCategoryFilter);
      setFamilyFilter('all');
      setSubfamilyFilter('all');
      setCurrentPage(1);
    }
  }, [selectedCategoryFilter]);

  // Efecto para manejar los parámetros de URL (categoría, familia, subfamilia) y el historial
  useEffect(() => {
    const categoriaParam = searchParams.get('categoria');
    const familiaParam = searchParams.get('familia');
    const subfamiliaParam = searchParams.get('subfamilia');
    
    console.log('🔗 URL params detected:', {
      categoria: categoriaParam,
      familia: familiaParam,
      subfamilia: subfamiliaParam,
      categoryListLength: categoryList.length
    });
    
    // Limpiar el filtro de categoría del contexto si no hay categoría en la URL
    if (!categoriaParam) {
      clearCategoryFilter();
    }
    
    // Sincronizar filtros con parámetros de URL
    if (categoriaParam) {
      console.log('🏷️ Setting category filter to:', categoriaParam);
      setCategoryFilter(categoriaParam);
      
      const currentCategory = categoryList.find(cat => cat.name === categoriaParam);
      console.log('📋 Category found in list:', currentCategory);
      
      if (currentCategory) {
        categoryHistory.add({
          id: String(currentCategory.id),
          name: currentCategory.name,
          image_url: getImageUrl(currentCategory.image_url || null)
        });
      }
    } else {
      // Si no hay categoría en URL, resetear a 'all'
      setCategoryFilter('all');
    }
    
    if (familiaParam) {
      setFamilyFilter(familiaParam);
    } else {
      // Si no hay familia en URL, resetear a 'all'
      setFamilyFilter('all');
    }
    
    if (subfamiliaParam) {
      setSubfamilyFilter(subfamiliaParam);
    } else {
      // Si no hay subfamilia en URL, resetear a 'all'
      setSubfamilyFilter('all');
    }
  }, [searchParams, categoryList, clearCategoryFilter]);

  // Cargar productos del backend con filtros
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        
        // Obtener parámetros de URL
        const categoriaParam = searchParams.get('categoria');
        const familiaParam = searchParams.get('familia');
        const subfamiliaParam = searchParams.get('subfamilia');
        const buscarParam = searchParams.get('buscar');
        
        console.log('🔄 Fetching products with params:', {
          categoria: categoriaParam,
          familia: familiaParam,
          subfamilia: subfamiliaParam,
          buscar: buscarParam
        });
        
        // Preparar filtros para el backend
        const filters: any = {};
        
        // Convertir nombres a IDs para el backend
        if (categoriaParam && categoriaParam !== 'all') {
          const category = categoryList.find(cat => cat.name === categoriaParam);
          console.log('🔍 Looking for category:', categoriaParam, 'Found:', category);
          if (category) {
            filters.category = category.id;
          }
        }
        
        if (familiaParam && familiaParam !== 'all') {
          const family = familyList.find(fam => fam.name === familiaParam);
          if (family) {
            filters.family = family.id;
          }
        }
        
        if (subfamiliaParam && subfamiliaParam !== 'all') {
          const subfamily = subfamilyList.find(sub => sub.name === subfamiliaParam);
          if (subfamily) {
            filters.subfamily = subfamily.id;
          }
        }
        
        if (buscarParam) {
          filters.search = buscarParam;
        }
        
        console.log('📊 Final filters for backend:', filters);
        
        // Si hay filtros, usar la función con filtros, sino obtener todos
        let productsResponse;
        if (Object.keys(filters).length > 0) {
          console.log('🎯 Using filtered products');
          productsResponse = await productService.getProductsWithFilters({
            ...filters,
            limit: 10000 // Obtener todos los productos filtrados
          });
          console.log('📦 Filtered products received:', productsResponse.products.length);
          setProducts(productsResponse.products);
          setFilteredProducts(productsResponse.products);
          setTotalProducts(productsResponse.total);
        } else {
          console.log('📦 Using all products');
          const productsArray = await productService.getAllProducts();
          console.log('📦 All products received:', productsArray.length);
          setProducts(productsArray);
          setFilteredProducts(productsArray);
          setTotalProducts(productsArray.length);
        }
      } catch (error) {
        console.error('Error loading products:', error);
        toast.error('Error al cargar los productos');
        setProducts([]);
        setFilteredProducts([]);
        setTotalProducts(0);
      } finally {
        setLoading(false);
      }
    };

    // Solo ejecutar cuando tengamos las listas de categorías, familias y subfamilias cargadas
    // o cuando no hay filtros específicos
    const categoriaParam = searchParams.get('categoria');
    const familiaParam = searchParams.get('familia');
    const subfamiliaParam = searchParams.get('subfamilia');
    
    const needsCategories = categoriaParam && categoriaParam !== 'all';
    const needsFamilies = familiaParam && familiaParam !== 'all';
    const needsSubfamilies = subfamiliaParam && subfamiliaParam !== 'all';
    
    const hasRequiredData = 
      (!needsCategories || categoryList.length > 0) &&
      (!needsFamilies || familyList.length > 0) &&
      (!needsSubfamilies || subfamilyList.length > 0);
    
    if (hasRequiredData) {
      fetchProducts();
    }
  }, [searchParams, categoryList, familyList, subfamilyList]);

  // Filtrar y ordenar productos (aplicar todos los filtros locales)
  useEffect(() => {
    const productsArray = Array.isArray(products) ? products : [];
    
    let filtered = productsArray;
    
    // Aplicar filtro de búsqueda local (SIEMPRE aplicar si hay searchTerm)
    const buscarParam = searchParams.get('buscar');
    if (searchTerm) {
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Aplicar filtro de categoría (solo si no viene de URL)
    const categoriaParam = searchParams.get('categoria');
    if (categoryFilter !== 'all' && !categoriaParam) {
      filtered = filtered.filter(product => 
        product.category_name === categoryFilter
      );
    }

    // Aplicar filtro de familia (solo si no viene de URL)
    const familiaParam = searchParams.get('familia');
    if (familyFilter !== 'all' && !familiaParam) {
      filtered = filtered.filter(product => 
        product.family_name === familyFilter
      );
    }

    // Aplicar filtro de subfamilia (solo si no viene de URL)
    const subfamiliaParam = searchParams.get('subfamilia');
    if (subfamilyFilter !== 'all' && !subfamiliaParam) {
      filtered = filtered.filter(product => 
        product.subfamily_name === subfamilyFilter
      );
    }

    // Ordenar productos
    filtered.sort((a, b) => {
      switch (sortOrder) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'category':
          return (a.category_name || '').localeCompare(b.category_name || '');
        case 'brand':
          return (a.brand || '').localeCompare(b.brand || '');
        default:
          return 0;
      }
    });

    setFilteredProducts(filtered);
    setCurrentPage(1); // Resetear a la primera página cuando cambian los filtros
  }, [products, searchTerm, categoryFilter, familyFilter, subfamilyFilter, sortOrder, searchParams]);

  // Funciones de paginación
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleProductsPerPageChange = (newProductsPerPage: number) => {
    setProductsPerPage(newProductsPerPage);
    setCurrentPage(1);
  };
  useEffect(() => {
    const buscarParam = searchParams.get('buscar');
    if (buscarParam) {
      setSearchTerm(buscarParam);
    }
  }, [searchParams]);

  // Generar números de página para mostrar
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          Cargando productos...
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Nuestros Productos</Title>
        <Subtitle>
          Descubre nuestra amplia gama de productos de alta calidad para todas tus necesidades
        </Subtitle>
      </Header>

      <ProductsLayout>
        {/* Overlay para filtros móviles */}
        <MobileFilterOverlay isOpen={isMobileFiltersOpen} onClick={closeMobileFilters} />
        
        {/* Columna de filtros a la izquierda */}
        <FiltersContainer isOpen={isMobileFiltersOpen}>
          <MobileFilterHeader>
            <MobileFilterTitle>
              <FaFilter /> Filtros
            </MobileFilterTitle>
            <MobileFilterClose onClick={closeMobileFilters}>
              ✕
            </MobileFilterClose>
          </MobileFilterHeader>
          
          <FilterSectionTitle>
            🔍 Búsqueda
          </FilterSectionTitle>
          
          <FilterGroup>
            <FilterLabel>Buscar productos:</FilterLabel>
            <DynamicSearchInput
              colors={colors}
              type="text"
              placeholder="Buscar por nombre o descripción..."
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                // Solo búsqueda local, sin actualizar URL
              }}
            />
          </FilterGroup>
          
          <FilterSectionTitle>
            📂 Categorización
          </FilterSectionTitle>
          
          <FilterGroup>
            <FilterLabel>
              <FaChevronDown /> Categoría:
            </FilterLabel>
            <FilterSelect
              value={categoryFilter}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              <option value="all">Todas las categorías</option>
              {categoryList.map(category => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>
              <FaChevronDown /> Familia:
            </FilterLabel>
            <FilterSelect
              value={familyFilter}
              onChange={(e) => handleFamilyChange(e.target.value)}
              disabled={availableFamilies.length === 0}
            >
              <option value="all">Todas las familias</option>
              {availableFamilies.map(family => (
                <option key={family.id} value={family.name}>
                  {family.name}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>
              <FaChevronDown /> Subfamilia:
            </FilterLabel>
            <FilterSelect
              value={subfamilyFilter}
              onChange={(e) => handleSubfamilyChange(e.target.value)}
              disabled={availableSubfamilies.length === 0}
            >
              <option value="all">Todas las subfamilias</option>
              {availableSubfamilies.map(subfamily => (
                <option key={subfamily.id} value={subfamily.name}>
                  {subfamily.name}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterSectionTitle>
            ⚙️ Configuración
          </FilterSectionTitle>

          <FilterGroup>
            <FilterLabel>Ordenar por:</FilterLabel>
            <FilterSelect
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="name">Nombre</option>
              <option value="category">Categoría</option>
              <option value="brand">Marca</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Productos por página:</FilterLabel>
            <ProductsPerPageSelect
              value={productsPerPage}
              onChange={(e) => handleProductsPerPageChange(Number(e.target.value))}
            >
              <option value={6}>6</option>
              <option value={12}>12</option>
              <option value={24}>24</option>
              <option value={48}>48</option>
            </ProductsPerPageSelect>
          </FilterGroup>

          <FilterResultsCount>
            📊 {filteredProducts.length} productos encontrados
          </FilterResultsCount>

          <ClearFiltersButton onClick={clearAllFilters}>
            🗑️ Limpiar filtros
          </ClearFiltersButton>

          {/* Botón para aplicar filtros en móvil */}
          <ApplyFiltersButton onClick={applyFiltersAndClose}>
            🔍 Aplicar Filtros
          </ApplyFiltersButton>
        </FiltersContainer>

        {/* Contenido de productos a la derecha */}
        <ProductsContainer>
          {/* Botón para mostrar filtros en móvil */}
          <MobileFilterButton onClick={toggleMobileFilters}>
            <FaFilter />
            Filtros y Búsqueda
          </MobileFilterButton>
          
          {/* Información de productos */}
          <PaginationInfo>
            Mostrando {indexOfFirstProduct + 1} - {Math.min(indexOfLastProduct, filteredProducts.length)} de {filteredProducts.length} productos
          </PaginationInfo>

          {currentProducts.length === 0 ? (
            <NoProducts>
              No se encontraron productos que coincidan con los filtros seleccionados.
            </NoProducts>
          ) : (
            <>
              <ProductsGrid>
                {currentProducts.filter(product => Number(product.price || 0) > 0).map(product => (
                  <ProductCard key={product.id} product={product} hideStockInfo />
                ))}
              </ProductsGrid>

              {/* Controles de paginación */}
              {totalPages > 1 && (
                <PaginationContainer>
                  <PaginationControls>
                    {/* Botón anterior */}
                    <PaginationButton
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      ‹
                    </PaginationButton>

                    {/* Números de página */}
                    {getPageNumbers().map((pageNumber, index) => (
                      pageNumber === '...' ? (
                        <span key={`ellipsis-${index}`} style={{ padding: '0 0.5rem', color: '#7f8c8d' }}>...</span>
                      ) : (
                        <PaginationButton
                          key={pageNumber}
                          active={currentPage === pageNumber}
                          onClick={() => handlePageChange(pageNumber as number)}
                        >
                          {pageNumber}
                        </PaginationButton>
                      )
                    ))}

                    {/* Botón siguiente */}
                    <PaginationButton
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      ›
                    </PaginationButton>
                  </PaginationControls>
                </PaginationContainer>
              )}
            </>
          )}
        </ProductsContainer>
      </ProductsLayout>
    </Container>
  );
};

export default Products;


interface VisitedCategory {
  id: string;
  name: string;
  image_url: string;
  visitedAt: number;
}
